// frontend/src/farm/iso.mjs
// Isometric 2:1 diamond grid maths in the FarmWorld container's LOCAL space (the farm's
// bounding box is [0,w]×[0,h]). Pure — no Phaser, no config.js — so `node --test` covers it.
// Cell (0,0) is the top corner of the diamond; x runs down-right, y runs down-left.

export const TILE_W = 96;
export const TILE_H = 48;
const HW = TILE_W / 2;
const HH = TILE_H / 2;

// Where a crop sprite's bottom edge sits, measured DOWN from the cell's centre.
// The crop art is a cluster of plants trimmed to its own outline, so its lowest pixel is the
// front plant's base — which belongs just inside the cell's front corner (HH below the centre),
// not near the middle. Anchored at the middle, the front half of the soil stayed bare and the
// foliage hung off the back edge, which is what "the crops aren't on their plots" looked like.
export const CROP_BASE_Y = HH - 4;

// How far the endless lawn extends beyond the owned plots, in cells. The top margin is
// smaller than the rest on purpose: the lawn's top edge IS the horizon line the sky strip
// sits on (farm/FarmBackdrop.js), so it must be reachable on screen, not 12 cells up.
export const LAWN_MARGIN = 12;
export const HORIZON_MARGIN = 4;   // cells between the garden's top corner and the backdrop
// Local px below the horizon that stay free of lawn props: the near backdrop band (drawn in
// front of the world, farm/backdropBands.js) overlaps the lawn's top edge there, and a close
// bush must never be painted behind the far hedge. 72 covers the overlap down to zoom 0.5.
export const HORIZON_CLEAR = 72;

export function farmOrigin(size) { return { x: size * HW, y: 0 }; }

// Continuous (fractional) cell coordinates of a local point — localToCell without the floor.
// The diamond is 0 <= x <= size, 0 <= y <= size in this space, so how far OUTSIDE it a point
// lies is a plain Chebyshev distance to that box (in cells), which is what the lawn-prop
// band and the fence maths are phrased in.
export function localToCellF(lx, ly, size) {
  const o = farmOrigin(size);
  const u = (lx - o.x) / HW;
  const v = (ly - o.y) / HH;
  return { x: (v + u) / 2, y: (v - u) / 2 };
}

// Local px of a fractional cell coordinate (the inverse of localToCellF).
export function cellFToLocal(cx, cy, size) {
  const o = farmOrigin(size);
  return { x: o.x + (cx - cy) * HW, y: o.y + (cx + cy) * HH };
}

// How many cells outside the diamond a fractional cell coordinate lies (0 = inside).
export function cellsOutside(cx, cy, size) {
  const dx = Math.max(0 - cx, cx - size, 0);
  const dy = Math.max(0 - cy, cy - size, 0);
  return Math.max(dx, dy);
}

export function farmExtent(size) { return { w: size * TILE_W, h: size * TILE_H }; }

export function cellTop(x, y, size) {
  const o = farmOrigin(size);
  return { x: o.x + (x - y) * HW, y: o.y + (x + y) * HH };
}

export function cellCentre(x, y, size) {
  const t = cellTop(x, y, size);
  return { x: t.x, y: t.y + HH };
}

// The affine map u=(lx-ox)/HW, v=(ly-oy)/HH turns every diamond into a unit square, so a
// floor on (v+u)/2 and (v-u)/2 is exact cell hit-testing, edges included.
export function localToCell(lx, ly, size) {
  const o = farmOrigin(size);
  const u = (lx - o.x) / HW;
  const v = (ly - o.y) / HH;
  return { x: Math.floor((v + u) / 2), y: Math.floor((v - u) / 2) };
}

export function inFarm(cell, size) {
  return Number.isInteger(cell.x) && Number.isInteger(cell.y)
    && cell.x >= 0 && cell.y >= 0 && cell.x < size && cell.y < size;
}

export function cellKey(x, y) { return `${x},${y}`; }
export function parseKey(key) { const [x, y] = key.split(',').map(Number); return { x, y }; }

export function diamondPoints(cx, cy, w = TILE_W, h = TILE_H) {
  return [cx, cy - h / 2, cx + w / 2, cy, cx, cy + h / 2, cx - w / 2, cy];
}

// The dirt path outside the gate. The gate replaces the boundary segment between the home
// exit cell (0, size-1) and cell (0, size) — the cell straight through it — so the path is
// that cell and the four behind it, walking away from the farm down-left.
export const PATH_LEN = 5;
export function pathCells(size) {
  return Array.from({ length: PATH_LEN }, (_, i) => ({ x: 0, y: size + i }));
}

// The gate to Podwórko (spec 2026-10-22): the fence section of cell (gardenGateX(size), size-1) on the
// left→bottom edge. GARDEN_GATE_X lines up with Podwórko's own gate, so the path between them runs straight;
// a garden too small to have that section (8…11) has its gate on x = 7 instead and the path jogs (owner
// 2026-10-22: "bent only for small garden"). It moves once, when the garden grows to 12.
export const GARDEN_GATE_X = 11;
const SMALL_GARDEN_GATE_X = 7;
export function gardenGateX(size) { return size > GARDEN_GATE_X ? GARDEN_GATE_X : SMALL_GARDEN_GATE_X; }

// Which way out the character's cell leads: 'home' (the corner by the house), 'podworko' (the gate,
// only while Podwórko is unlocked) or null. Only the player's own steering takes them through
// (`manual`): a walk to a job, or to help on a friend's farm, used to drop you into the house when its
// path crossed the corner.
// `from`: the cell the character stood on last frame — an exit fires only when a step brings the feet ONTO
// the exit cell (a job walk that ended on the gate cell does not turn the next key press into an exit).
export function gardenExit(cell, size, { podworko = false, manual = true, from = null } = {}) {
  if (!manual) return null;
  if (from && from.x === cell.x && from.y === cell.y) return null;
  if (cell.x === 0 && cell.y === size - 1) return 'home';
  if (podworko && cell.x === gardenGateX(size) && cell.y === size - 1) return 'podworko';
  return null;
}

// Painter's order inside the objects container: lower on screen draws in front (ySort).
export function depthAt(localY) { return localY; }

// The exit cells stay walkable even under a farm object (a hut or a fence on the gate cell would
// otherwise make the gate a dead end — Podwórko has no button to fall back on).
export function isExitCell(cell, size, { podworko = false } = {}) {
  return gardenExit(cell, size, { podworko, manual: true }) !== null;
}
